import React, { useRef, useState, useEffect, useCallback } from "react";
import { FaSearch } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { Filters } from "./components/Filters/Filters";
import { IListItem } from "../../interfaces/IDocDetails";
import { ListItem } from "./components/ListItem/ListItem";
import { useFilterContext } from "../../../../context/FilterContext";
import { FilterState } from "../../interfaces/FilterState";
import { ModalConfirmWindow } from "../../../AdminPanelPage/components/ManageUserTab/components/ModalConfirmWindow/ModalConfirmWindow";
import { useFavoriteListContext } from "../../../../context/FavoriteListContext";
import { useLocation } from "react-router-dom";
import { VariableSizeList, ListOnItemsRenderedProps } from "react-window";

interface IListBlockProps {
  offsetCount: number;
  isLoading: boolean;
  isDisabled: boolean;
  data: {
    items: IListItem;
    filters: FilterState;
    total: number;
    exclude_ids: string[];
    load_more?: boolean;
  };
  listData: { items: IListItem[]; filters: FilterState };
  onFilterChange: (updatedFilters: FilterState) => void;
  onOffsetChange: (offsetCount: number) => void;
  onClearSearch: () => void;
  onItemTagsUpdate: (itemId: string, tags: string[]) => void;
}

const ListBlock: React.FC<IListBlockProps> = ({
  data,
  listData,
  isLoading,
  isDisabled,
  offsetCount,
  onOffsetChange,
  onFilterChange,
  onClearSearch,
  onItemTagsUpdate,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { filterState } = useFilterContext();
  const [tags, setTags] = useState<string[]>([]);
  const [windowHeight, setWindowHeight] = useState(() => window.innerHeight);
  const [listKey, setListKey] = useState(0);
  const removeTagCallbacks = useRef<{
    [itemId: string]: (idx: number) => void;
  }>({});
  const { favoriteList } = useFavoriteListContext();
  const [lastFilters, setLastFilters] = useState<FilterState>(data?.filters);
  const [modal, setModal] = useState({
    visible: false,
    itemId: null as string | null,
    tagIdx: null as number | null,
  });

  const handleFiltersChange = (updatedFilters: FilterState) => {
    onFilterChange(updatedFilters);
  };

  const handleLoadMore = useCallback(() => {
    if (!isDisabled && !isLoading) {
      onOffsetChange(offsetCount + 1);
    }
  }, [isDisabled, isLoading, offsetCount, onOffsetChange]);

  const listRef = useRef<VariableSizeList | null>(null);
  const itemHeights = useRef<Map<number, number>>(new Map());
  const [, setMeasureVersion] = useState(0);
  const SENTINEL_HEIGHT = 28;
  const DEFAULT_ROW_HEIGHT = 220;
  const getItemSize = (index: number) => {
    if (visibleItems && index >= visibleItems.length)
      return isLoading ? SENTINEL_HEIGHT : 0;
    return itemHeights.current.get(index) ?? DEFAULT_ROW_HEIGHT;
  };
  const handleLoadMoreRef = useRef(handleLoadMore);
  const loadTriggeredRef = useRef(false);

  const setItemSize = (index: number, size: number) => {
    const prev = itemHeights.current.get(index);
    if (prev !== size) {
      itemHeights.current.set(index, size);
      listRef.current?.resetAfterIndex(index);
      setMeasureVersion((v) => v + 1);
    }
  };

  const visibleItems = listData?.items.filter((item) =>
    location.pathname === "/favorite"
      ? favoriteList.some((fav) => fav.case_no === item.case_no)
      : true,
  );

  const estimatedContentHeight =
    (visibleItems || []).reduce((sum, _it, idx) => {
      return sum + (itemHeights.current.get(idx) ?? DEFAULT_ROW_HEIGHT);
    }, 0) + SENTINEL_HEIGHT;

  const windowListHeight = Math.max(120, windowHeight - 50);
  const approxContentHeight = (visibleItems?.length ?? 0) * DEFAULT_ROW_HEIGHT;
  const listRenderHeight =
    approxContentHeight >= windowListHeight
      ? windowListHeight
      : Math.min(windowListHeight, Math.max(estimatedContentHeight, 100));

  const Row = ({
    index,
    style,
  }: {
    index: number;
    style: React.CSSProperties;
  }) => {
    const rowRef = useRef<HTMLDivElement | null>(null);
    const item = visibleItems[index];
    const isLastRealItem = index === visibleItems.length - 1;

    useEffect(() => {
      if (!item || !rowRef.current) return;
      const h = rowRef.current.getBoundingClientRect().height;
      setItemSize(index, h);
    }, [item, index]);

    if (index >= visibleItems.length) {
      return (
        <div style={style}>
          <div
            className="flex justify-center items-center"
            style={{ height: isLoading ? SENTINEL_HEIGHT : 0 }}
          >
            {isLoading && (
              <span className="dark:text-white">
                {t("adminPanel.loading")}...
              </span>
            )}
          </div>
        </div>
      );
    }

    return (
      <div style={style}>
        <div ref={rowRef} className={isLastRealItem ? "" : "pb-3"}>
          <ListItem
            item={item}
            onTagsUpdate={(data: string[]) => {
              setTags(data);
              if (item?.id) onItemTagsUpdate(item.id, data);
            }}
            onRequestRemoveTag={(tagIdx, callback) => {
              removeTagCallbacks.current[item.id] = callback;
              handleOpenModal(item.id, tagIdx);
            }}
            tagsData={data?.filters.tags || listData?.filters.tags}
          />
        </div>
      </div>
    );
  };

  const handleOpenModal = (itemId: string, tagIdx: number) => {
    setModal({ visible: true, itemId, tagIdx });
  };

  const handleCloseModal = () =>
    setModal({ visible: false, itemId: null, tagIdx: null });

  const handleConfirmRemoveTag = () => {
    if (modal.itemId && modal.tagIdx != null) {
      removeTagCallbacks.current[modal.itemId]?.(modal.tagIdx);
    }
    handleCloseModal();
  };

  useEffect(() => {
    handleLoadMoreRef.current = handleLoadMore;
  }, [handleLoadMore]);

  useEffect(() => {
    if (data?.filters) {
      setLastFilters(data.filters);
    }
  }, [data?.filters]);

  useEffect(() => {
    if (!isLoading) {
      loadTriggeredRef.current = false;
    }
  }, [isLoading]);

  // Backend signals a chunked-document page collapsed to too few distinct items
  useEffect(() => {
    if (
      data?.load_more &&
      !isLoading &&
      !isDisabled &&
      !loadTriggeredRef.current
    ) {
      loadTriggeredRef.current = true;
      handleLoadMore();
    }
  }, [data?.load_more, isLoading, isDisabled, handleLoadMore]);

  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
      itemHeights.current.clear();
      listRef.current?.resetAfterIndex(0, true);
      setMeasureVersion((v) => v + 1);
      setListKey((k) => k + 1);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      <div className="w-1/4 flex flex-col min-h-0 overflow-hidden rounded-lg">
        <Filters
          filtersData={lastFilters}
          filters={filterState}
          tags={tags}
          onFiltersChange={handleFiltersChange}
          search={filterState.search || ""}
          onClearSearch={onClearSearch}
          items={listData?.items || []}
        />
      </div>
      <div className="w-3/4 flex flex-col overflow-x-hidden overflow-hidden min-h-0">
        {listData?.items.length > 0 ? (
          <>
            <VariableSizeList
              key={listKey}
              ref={listRef}
              height={listRenderHeight}
              itemCount={
                visibleItems.length +
                (data?.total > visibleItems.length ? 1 : 0)
              }
              itemSize={getItemSize}
              width="100%"
              onItemsRendered={({
                visibleStopIndex,
              }: ListOnItemsRenderedProps) => {
                const isLastItemVisible =
                  visibleStopIndex === visibleItems.length - 1;

                const hasMore = (data?.total ?? 0) > visibleItems.length;

                if (
                  isLastItemVisible &&
                  hasMore &&
                  !isLoading &&
                  !isDisabled &&
                  !loadTriggeredRef.current
                ) {
                  loadTriggeredRef.current = true;
                  onOffsetChange(offsetCount + 1);
                }
              }}
            >
              {Row}
            </VariableSizeList>
          </>
        ) : (
          !isLoading && (
            <div className="flex flex-col items-center gap-6 h-full justify-center">
              <FaSearch size={90} color="grey" />
              <div className="text-2xl dark:text-white">
                {t("docDetails.noResultsFound")}
              </div>
            </div>
          )
        )}
      </div>
      {modal.visible && (
        <div className="-ml-3">
          <ModalConfirmWindow
            setShowModal={handleCloseModal}
            handleDeleteUser={handleConfirmRemoveTag}
            message={t("docInfo.confirmRemoveTag")}
          />
        </div>
      )}
    </>
  );
};

export { ListBlock };
